import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, CheckCircle2 } from 'lucide-react';

// ── Feedesk-specific components (src/components/feedesk/)
import FeedeskHero from '../components/feedesk/FeedeskHero';
import FeedeskPricingContent from '../components/feedesk/FeedeskPricingContent';

// ── Shared component reused from Sparklio
import ImageCarousel from '../components/projects/ImageCarousel';

// ── Import Feedesk-specific dashboard images ──
import FeedeskDashboard from '../assets/feedesk-imgs/FeedeskDashboard.png';
import FeedeskReports from '../assets/feedesk-imgs/FeedeskReports.png';
import FeedeskPayments from '../assets/feedesk-imgs/FeedeskPayments.png';

import styles from './FeedeskPage.module.css';

const fadeInUp = {
  hidden:  { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const FeedeskPage = ({ setIsRegisterOpen }) => {
  const [activeFeatureTab, setActiveFeatureTab] = useState(0);

  const tabbedFeatures = [
    {
      title: 'Fee Setup',
      desc: 'Create tuition, exam, and batch fees. Schedule recurring dues and one-time charges for every class and student segment.',
      slides: [
        { title: 'Fee Dashboard',  imgUrl: FeedeskDashboard },
        { title: 'Batch Fee View', imgUrl: FeedeskDashboard },
      ],
    },
    {
      title: 'Payment Collection',
      desc: "Record cash, cheque, UPI, and online payments instantly. Auto-generate receipts and keep every student ledger accurate in real time.",
      slides: [
        { title: 'Student Ledger',   imgUrl: FeedeskPayments },
        { title: 'Receipt History', imgUrl: FeedeskPayments },
      ],
    },
    {
      title: 'Reports & Growth',
      desc: 'Get instant defaulter lists, collection summaries, and academy-ready statements. Share performance reports with staff and owners in one click.',
      slides: [
        { title: 'Reports & Analytics',        imgUrl: FeedeskReports },
        { title: 'Collection Summary', imgUrl: FeedeskReports },
      ],
    },
  ];

  return (
    <>
      {/* ── 1. HERO ── */}
      <FeedeskHero setIsRegisterOpen={setIsRegisterOpen} />

      <div className={styles.pageContainer}>

        {/* ── 2. PRICING ── */}
        <FeedeskPricingContent setIsRegisterOpen={setIsRegisterOpen} />

        {/* ── 3. TABBED CAROUSEL ── */}
        <section className={styles.tabbedSection}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className={styles.sectionHeader}
          >
            <h2>Everything Your Academy Needs</h2>
            <p>
              From issuing the first tuition invoice to closing monthly accounts,
              Feedesk covers every step of your academy's fee lifecycle.
            </p>
          </motion.div>

          <div className={styles.tabsContainer}>
            <div className={styles.tabButtons}>
              {tabbedFeatures.map((tab, index) => (
                <button
                  key={index}
                  className={`${styles.tabBtn} ${activeFeatureTab === index ? styles.activeTab : ''}`}
                  onClick={() => setActiveFeatureTab(index)}
                >
                  {tab.title}
                </button>
              ))}
            </div>
            <div className={styles.tabContentDesc}>
              <p>{tabbedFeatures[activeFeatureTab].desc}</p>
            </div>
            <ImageCarousel slides={tabbedFeatures[activeFeatureTab].slides} />
          </div>
        </section>

        {/* ── 4. TESTIMONIALS ── */}
        <section className={styles.testimonialsSection}>
          <div className={styles.sectionHeader}>
            <h2>Trusted by Coaching Owners &amp; Admins</h2>
          </div>
          <div className={styles.testimonialGrid}>
            {[
              {
                name: 'Rita Sharma',
                role: 'Founder, Excel Coaching, Delhi',
                quote: 'Managing batch fees used to take hours after every class. Feedesk now gives me clear student ledgers and instant receipts.',
                avatarUrl: 'https://ui-avatars.com/api/?name=Rita+Sharma&background=1565C0&color=fff&rounded=true&bold=true',
              },
              {
                name: 'Vikram Joshi',
                role: 'Director, Peak Academy, Pune',
                quote: 'We handle multiple batches and fee categories. Feedesk replaced spreadsheets and brought complete visibility to our accounts.',
                avatarUrl: 'https://ui-avatars.com/api/?name=Vikram+Joshi&background=2E7D32&color=fff&rounded=true&bold=true',
              },
              {
                name: 'Meera Iyer',
                role: 'Owner, Bright Minds Tuition',
                quote: 'Year-end statements are now ready in minutes. Our auditors love the clean export and the parents love the quick receipts.',
                avatarUrl: 'https://ui-avatars.com/api/?name=Meera+Iyer&background=E65100&color=fff&rounded=true&bold=true',
              },
              {
                name: 'Sanjay Rao',
                role: 'Head Coach, Future Leaders Academy',
                quote: 'The collection rate went from 70% to 95% after we started sending automated reminders. No more chasing students.',
                avatarUrl: 'https://ui-avatars.com/api/?name=Sanjay+Rao&background=6A1B9A&color=fff&rounded=true&bold=true',
              },
            ].map((item, index) => (
              <motion.div whileHover={{ y: -10 }} className={styles.testiCard} key={index}>
                <div className={styles.quoteIcon}>"</div>
                <p>{item.quote}</p>
                <div className={styles.testiUser}>
                  <img src={item.avatarUrl} alt={item.name} className={styles.userAvatar} />
                  <div>
                    <strong>{item.name}</strong><br />
                    <span>{item.role}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── 5. AI SECTION ── */}
        <section className={styles.aiSection}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <Sparkles size={60} className={styles.aiIcon} />
            <h2>Smarter Fee Collection</h2>
            <p>
              Feedesk automatically calculates each student's fee, flags overdue accounts, and sends timely reminders —
              so your academy spends time teaching, not chasing payments.
            </p>
          </motion.div>
        </section>

        {/* ── 6. SPLIT SECTION ── */}
        <section className={styles.splitSection}>
          <div className={styles.container}>
            <div className={styles.splitLayoutLeftText}>
              <motion.div
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
                className={styles.splitTextContent}
              >
                <h2>Full Financial Clarity, <span className={styles.blueText}>Zero Guesswork.</span></h2>
                <p>
                  Every rupee collected, every due pending, every expense shared — tracked
                  automatically and available as a professional report whenever your academy needs it.
                </p>
                <ul className={styles.checklist}>
                  <li><CheckCircle2 size={18} /> <strong>Instant defaulter lists:</strong> see exactly which students or batches are overdue.</li>
                  <li><CheckCircle2 size={18} /> <strong>Auto receipt generation:</strong> PDF receipts sent to students the moment payment is recorded.</li>
                  <li><CheckCircle2 size={18} /> <strong>Batch fee calculator:</strong> split fees across classes, exam sessions, and special programs.</li>
                  <li><CheckCircle2 size={18} /> <strong>Audit-ready statements:</strong> monthly and annual financial reports for owners and auditors.</li>
                </ul>
              </motion.div>
              <div className={styles.splitImageContent}>
                <ImageCarousel
                  slides={[
                    { title: 'Payment Report',        imgUrl: FeedeskReports },
                    { title: 'Collection Analytics', imgUrl: FeedeskReports },
                  ]}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── 7. IMPACT STATS ── */}
        <section className={styles.footerSection}>
          <div className={styles.growthHeader}>
            <h2>The Impact of Digital Fee Collection</h2>
          </div>
          <div className={styles.statsGrid}>
            <motion.div initial="hidden" whileInView="visible" variants={fadeInUp}
              className={styles.statCard} style={{ background: '#e0e7ff' }}>
              <h3>96%</h3><p>Collection Rate</p><small>Academies report near-complete monthly recovery</small>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" variants={fadeInUp} transition={{ delay: 0.2 }}
              className={styles.statCard} style={{ background: '#cffafe' }}>
              <h3>5 min</h3><p>Monthly Billing Cycle</p><small>Raise fees for all batches in under five minutes</small>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" variants={fadeInUp} transition={{ delay: 0.4 }}
              className={styles.statCard} style={{ background: '#ffedd5' }}>
              <h3>100%</h3><p>Transparent Ledger</p><small>Every transaction tracked, every receipt accounted for</small>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default FeedeskPage;